import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import imageCompression from "browser-image-compression";
import _, { get, toLower } from "lodash";
import { parsePhoneNumber } from "react-phone-number-input";
import { updateCognitoUserAccounts } from "./authUtility";
import { isStarted, linkAccount, Operation, processDirectLink } from "./axle";
import { MAX_FILE_SIZE, MAX_HITL_STARTED_RETRIES, MAX_MEGAPIXELS } from "./constants";
import { MANUAL_ERROR_MESSAGES, ManualErrorType } from "./errorConstants";
import { getInvalidFieldError, getMissingFieldError } from "./fieldCopy";

export const generateLinkErrorMessage = (linkError: string) => {
  return `The Axle service could not ${linkError} for the specified carrier.`;
};

export const generateManualErrorMessage = (
  manualError: ManualErrorType | string
) => {
  // Handle known manual error types with improved messages
  if (manualError in MANUAL_ERROR_MESSAGES) {
    return MANUAL_ERROR_MESSAGES[manualError as ManualErrorType];
  }

  // Fallback for API error messages and other unknown strings
  return `The Axle service could not ${manualError} the uploaded document`;
};

export const generateIgnitionErrorMessage = (ignitionError: string) => {
  return `Invalid token provided. Please ${ignitionError}`;
};

export const checkZipCode = (str: string) => {
  // Check if the string consists of exactly 5 numeric characters
  return /^\d{5}$/.test(str);
};

export const checkIgnitionToken = (ignitionToken: string) => {
  const ignitionTokenValidation = {
    message: "",
    status: true,
  };
  // Check if the string starts with "ign_"
  const startsWithIgn = _.startsWith(ignitionToken, "ign_");

  if (!startsWithIgn) {
    _.set(
      ignitionTokenValidation,
      "message",
      generateIgnitionErrorMessage("ensure ignition token starts with 'ign_'")
    );
    _.set(ignitionTokenValidation, "status", false);

    return ignitionTokenValidation;
  }

  // Check if the string contains exactly 25 characters
  const validLength = _.size(ignitionToken) === 25;

  if (!validLength) {
    _.set(
      ignitionTokenValidation,
      "message",
      generateIgnitionErrorMessage(
        "ensure ignition token is 25 characters long"
      )
    );
    _.set(ignitionTokenValidation, "status", false);
  }

  return ignitionTokenValidation;
};

export const getUserAndMetadataFromQueryParams = (
  obj: Record<string, any>,
  userKeys: string[]
) => {
  const extractedUserItems = _.pick(obj, userKeys);
  const extractedMetadataKeys = _.difference(_.keys(obj), userKeys);
  const extractedMetadataItems = _.pick(obj, extractedMetadataKeys);

  // Parse phone number
  if (extractedUserItems.phone) {
    extractedUserItems.phone = parsePhoneNumber(
      extractedUserItems.phone,
      "US"
    )?.number;
  }

  // Return extracted user fields
  return {
    userQueryParams: extractedUserItems,
    metadataQueryParams: extractedMetadataItems,
  };
};

/**
 * Compress an image file to fit strictly within both:
 *   1) MAX_MEGAPIXELS by scaling resolution if necessary
 *   2) MAX_FILE_SIZE (in MB) by performing a binary search on the quality
 *
 * @param {File} file        The original image file.
 * @param {string} mimeType  The target MIME type (e.g., 'image/jpeg', 'image/png').
 * @returns {Promise<File>}   A promise that resolves to a compressed File object.
 */
export const compressImage = async (file: any, mimeType: string) => {
  console.log(
    `Compressing image to <= ${MAX_MEGAPIXELS} MP and <= ${MAX_FILE_SIZE} MB...`
  );

  try {
    const sizeInMB = file.size / (1024 * 1024);
    console.log(`Original file size: ${sizeInMB.toFixed(2)} MB`);

    if (sizeInMB === 0) {
      console.warn("Empty file found...");
    }

    // Get image dimensions
    const [img] = await imageCompression.drawFileInCanvas(file);
    const width = img.width;
    const height = img.height;
    const currentMegaPixels = (width * height) / 1_000_000;
    console.log(`Original megapixels: ${currentMegaPixels.toFixed(2)}`);

    // Check if file already meets both constraints
    if (currentMegaPixels <= MAX_MEGAPIXELS && sizeInMB <= MAX_FILE_SIZE) {
      console.log("Image already under threshold; returning original file.");
      return file;
    }

    // Reduce dimensions so we don't exceed max MP
    let scaleFactor = 1;
    if (currentMegaPixels > MAX_MEGAPIXELS) {
      scaleFactor = Math.sqrt(MAX_MEGAPIXELS / currentMegaPixels);
    }
    const targetWidth = Math.round(width * scaleFactor);
    const targetHeight = Math.round(height * scaleFactor);

    // Helper function that compresses at a given quality
    async function compressAtQuality(quality: number) {
      const options = {
        fileType: mimeType,
        initialQuality: quality,
        maxWidthOrHeight: Math.max(targetWidth, targetHeight),
        maxIteration: 1,
        useWebWorker: true,
      };
      const compressed = await imageCompression(file, options);
      return compressed;
    }

    // Binary search to find the highest quality that produces <= MAX_FILE_SIZE MB
    let lowerQuality = 0.01; // Minimum reasonable quality
    let upperQuality = 1.0; // Maximum (best) quality
    let bestFile = null;
    let bestFileSizeMB = Infinity;

    const MAX_ITERATIONS = 8;
    for (let i = 0; i < MAX_ITERATIONS; i++) {
      const midQuality = (lowerQuality + upperQuality) / 2;
      const testFile = await compressAtQuality(midQuality);
      const testFileSizeMB = testFile.size / (1024 * 1024);

      // If the file fits under the max size, try to go higher (improve quality).
      if (testFileSizeMB <= MAX_FILE_SIZE) {
        bestFile = testFile;
        bestFileSizeMB = testFileSizeMB;
        lowerQuality = midQuality;
      } else {
        // Otherwise, go lower to get a smaller file.
        upperQuality = midQuality;
      }

      // Stop early if we're close enough to the target (e.g., within 0.01 MB)
      if (Math.abs(testFileSizeMB - MAX_FILE_SIZE) < 0.01) {
        break;
      }
    }

    // If after binary search we never found a file <= max size, bestFile might remain null
    if (!bestFile) {
      // Use the final attempt from compressAtQuality(upperQuality) or just fallback
      console.warn(
        "Could not get below the target file size, returning best attempt possible."
      );
      bestFile = await compressAtQuality(lowerQuality);
      bestFileSizeMB = bestFile.size / (1024 * 1024);
    }

    console.log(`Final compressed size: ${bestFileSizeMB.toFixed(2)} MB`);
    return bestFile;
  } catch (error) {
    console.error("Error compressing image:", error);
    throw error;
  }
};

// Define a custom hook that can be used by both EnterMfa and Login components
// This hook handles getAccount logic, including retries and error handling
export const useAccountHandler = (
  {
    ignitionToken,
    integration,
    setAccountInfo,
    loginInformation,
    nextStep,
    setLinkError,
    setShowNav,
    retryGetAccountAttempts,
  }: {
    ignitionToken: string;
    integration: string;
    setAccountInfo: (accountInfo: Record<string, any>) => void;
    loginInformation: Record<string, any>;
    nextStep: (step: string, noHistory?: boolean) => void;
    setLinkError: (errorMessage: string) => void;
    setShowNav: (showNav: boolean) => void;
    retryGetAccountAttempts: number;
  },
  setLoginInformation?: (loginInfo: Record<string, any> | null) => void
) => {
  /**
   * Checks if a user is currently authenticated with Cognito
   * @returns {Promise<boolean>} True if user is authenticated and has valid tokens, false otherwise
   */
  const checkAuthStatus = async () => {
    try {
      await getCurrentUser(); // Will throw if no user is authenticated
      const { tokens } = await fetchAuthSession();
      return tokens && tokens.idToken ? true : false;
    } catch (error) {
      return false;
    }
  };

  /**
   * Retries the getAccount function if an error occurs
   * @returns {Promise<void>}
   */
  const retryGetAccount = async () => {
    if (retryGetAccountAttempts < 1) {
      retryGetAccountAttempts++;
      console.log("Received an error from getAccount, retrying...");
      await getAccount();
    } else {
      setLinkError("retrieve account details");
      nextStep("carrier-error");
    }
  };

  /**
   * Handles errors returned from the getAccount/linkAccount function
   * @param {Object} error - The error object returned from linkAccount
   * @returns {Promise<void>}
   */
  const handleUnsuccessfulGetAccount = async (error: any) => {
    switch (get(error, "code")) {
      case 400:
        if (
          error.message ===
          "Sorry, your insurance account or policy has not been set up for online access."
        ) {
          nextStep("account-pending");
        } else {
          console.error("Unknown 400 error from getAccount: ", error);
          setLinkError("retrieve account details");
          nextStep("carrier-error");
        }
        break;
      case 503:
        nextStep("carrier-maintenance");
        break;
      case 401:
      case 403:
      case 500:
        console.log(`${get(error, "code")} error from getAccount: `, error);
        await retryGetAccount();
        break;
      default:
        console.error("Unknown error from getAccount: ", error);
        await retryGetAccount();
    }
  };

  /**
   * Fetches account details using linkAccount and handles the response
   * @param {boolean} isReturningLink - Whether this is a returning link flow
   * @returns {Promise<void>}
   */
  const getAccount = async (
    isReturningLink?: boolean,
    startedPollAttempts: number = 0
  ) => {
    // Fetch account
    let linkAccountResponse, linkAccountError;

    if (isIntegrationV123(loginInformation, integration)) {
      const [directLinkResponse, directLinkError] = await processDirectLink(ignitionToken, integration, Operation.GET_ACCOUNT, {});
      // Still extracting: constructedAccount is absent because the account does
      // not exist yet, which reads downstream as "no policies".
      if (!directLinkError && isStarted(directLinkResponse)) {
        if (startedPollAttempts < MAX_HITL_STARTED_RETRIES) {
          return getAccount(isReturningLink, startedPollAttempts + 1);
        }
        setLinkError("retrieve account details");
        nextStep("carrier-error");
        setShowNav(true);
        return;
      }
      linkAccountResponse = get(directLinkResponse, "constructedAccount");
      linkAccountError = directLinkError;
    } else {
      [linkAccountResponse, linkAccountError] = await linkAccount(ignitionToken);
    }

    if (linkAccountError) {
      // Handle unsuccessful getAccount
      await handleUnsuccessfulGetAccount(linkAccountError);

      // Restore the nav bar
      setShowNav(true);
    } else {
      // If no compatible policies on account send to "no-policies"
      if (get(linkAccountResponse, "policies.length", 0) === 0) {
        nextStep("no-policies", isReturningLink);
        return;
      }

      // Save account info to state
      setAccountInfo(linkAccountResponse);

      // If a user is logged in, update their saved accounts list in Cognito with the new account
      const isUserAuthenticated = await checkAuthStatus();

      if (!isReturningLink && isUserAuthenticated) {
        // Update the user's accounts list in Cognito with the new account
        await updateCognitoUserAccounts(linkAccountResponse.id);
      }

      // setLoginInformation is not passed in at the EnterMfa step, so only perform this logic when the previous step was Login
      if (setLoginInformation) {
        // Set login result to link
        setLoginInformation({
          ...loginInformation,
          result: "link",
          resultDetail: "user-portal",
        });
      }

      // Go to policy selection step
      nextStep("confirm", isReturningLink);

      // Restore the nav bar
      setShowNav(true);
    }
  };

  return getAccount;
};

// Function performs input validation on the user input fields listed in a carrier's linkConfig
// This includes fields such as policy number, last 5 of VIN, postal code, etc.
export const validateIntegrationUserFields = (
  userFields: Record<string, any>,
  userInputFieldsConfig: Array<Record<string, any>>
) => {
  let isValid = true;
  let errorMessage = "";
  for (const inputField of userInputFieldsConfig) {
    const inputFieldValue = userFields[inputField.value];
    // check if input field is empty
    if (!inputFieldValue) {
      isValid = false;
      errorMessage = getMissingFieldError(inputField);
      return [isValid, errorMessage, inputField.value];
    }

    // Check if input field is the correct length
    if (
      inputField.minLength &&
      inputField.maxLength &&
      !(inputField.minLength <= inputFieldValue.length <= inputField.maxLength)
    ) {
      isValid = false;
      errorMessage = getInvalidFieldError(inputField);
      return [isValid, errorMessage, inputField.value];
    } else if (
      // If only minLength is available, check if input field is too short
      inputField.minLength &&
      !(inputField.minLength <= inputFieldValue.length)
    ) {
      isValid = false;
      errorMessage = getInvalidFieldError(inputField);
      return [isValid, errorMessage, inputField.value];
    } else if (
      // If only maxLength is available, check if input field is too long
      inputField.maxLength &&
      !(inputFieldValue.length <= inputField.maxLength)
    ) {
      isValid = false;
      errorMessage = getInvalidFieldError(inputField);
      return [isValid, errorMessage, inputField.value];
    }
  }

  return [isValid, errorMessage];
};

/**
 * Formats a phone number to mask all digits except the last 4
 * @param {string} phone The phone number to format
 * @returns {string} Formatted phone number with masked digits
 */
export const formatPhoneNumber = (phone: string) => {
  if (!phone) return "";

  // Get the last 4 digits
  const lastFour = phone.slice(-4);

  // Count remaining digits to mask
  const remainingDigits = phone.length - 4;
  const maskedDigits = "•".repeat(remainingDigits);

  return `${maskedDigits} ${lastFour}`;
};

/**
 * Formats seconds into MM:SS format
 * @param {number} seconds Total seconds
 * @returns {string} Formatted time string
 */
export const formatTime = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${seconds % 60 < 10 ? "0" : ""}${seconds % 60}`;

/**
 * Formats accepted document types into a grammatically correct error message fragment
 * @param {Array} acceptedDocs Array of accepted document objects with displayName property
 * @returns {string} Formatted string with proper articles and grammar
 */
export const formatAcceptedDocumentTypes = (
  acceptedDocs: Array<{ displayName: string }>
) => {
  if (!acceptedDocs || acceptedDocs.length === 0) {
    return "a supported document";
  }

  const displayNames = acceptedDocs.map((doc) => doc.displayName.toLowerCase());

  // Helper function to determine if we should use "an" instead of "a"
  const getArticle = (word: string) => {
    const vowels = ["a", "e", "i", "o", "u"];
    return vowels.includes(word.toLowerCase().charAt(0)) ? "an" : "a";
  };

  if (displayNames.length === 1) {
    return `${getArticle(displayNames[0])} ${displayNames[0]}`;
  }

  if (displayNames.length === 2) {
    return `${getArticle(displayNames[0])} ${displayNames[0]} or ${displayNames[1]
      }`;
  }

  // For 3 or more items
  const lastItem = displayNames.pop();
  return `${getArticle(displayNames[0])} ${displayNames.join(
    ", "
  )}, or ${lastItem}`;
};

export const getIntegrationConfig = (
  session: any,
  loginInformation: any,
  integration: any
) => {
  const sessionCarrierId = get(loginInformation, "carrier.id");
  // Retrieve the integration config from the carrier config
  const sessionCarrierConfig =
    session.carriers.find(
      (carrierConfig: any) => carrierConfig.id === sessionCarrierId
    ) || {};

  const integrationConfig =
    get(sessionCarrierConfig, "integrations", []).find(
      (integrationConfig: any) => integrationConfig.integration === integration
    ) || {};

  return integrationConfig;
};

export const shouldReachValidatePolicy = (session: any): boolean => {
  const validatePolicyConfigEnabled = get(
    session,
    "validate-policy.enabled",
    false
  );
  const isSandboxStage = toLower(process.env.REACT_APP_STAGE || "") === "sandbox";

  const acceptedClients =
    get(session, "client") === "cli_LvZhYfNAPkmaVw_K4vHv6" ||
    get(session, "client") === "cli_uFwaNdT3TwjZPaxVVAuCq";

  return isSandboxStage && (validatePolicyConfigEnabled || acceptedClients);
};

/**
 * Determines the next step after policy linking based on validation config and stage
 * @param {any} session - The session object containing validatePolicyConfig
 * @returns {string} "validate-policy" only in sandbox when validation is enabled for the session/client, otherwise "success"
 */
export const getNextStepBeforeSuccess = (session: any): string => {
  if (shouldReachValidatePolicy(session)) {
    return "validate-policy";
  }

  return "success";
};


export const isIntegrationV123 = (
  loginInformation: any,
  integration?: string,
  session?: any
) => {
  if (get(loginInformation, "type")) {
    const type = get(loginInformation, "type");
    const result = type === "V123";
    console.log("[isIntegrationV123] loginInformation.type exists and is V123: ", result);
    return result;
  } else if (get(loginInformation, "result") === "manual" || get(loginInformation, "result") === "link") {
    console.log(`[isIntegrationV123] loginInformation.result is: ${get(loginInformation, "result")}`);
    return false;
  }

  if (!_.size(session) && integration) {
    console.log("[isIntegrationV123] no session, checking loginInformation.carrier.integrations");
    const loginInformationIntegrations =
      get(loginInformation, "carrier.integrations", []) || [];
    const matchingLoginInformationIntegration =
      loginInformationIntegrations.find(
        (integrationConfig: any) =>
          integrationConfig.integration === integration
      );
    console.log(
      "[isIntegrationV123] matchingLoginInformationIntegration: ",
      matchingLoginInformationIntegration
    );
    const result = get(matchingLoginInformationIntegration, "type") === "V123";
    console.log("[isIntegrationV123] result: ", result);
    return result;
  }

  const sessionCarriers = get(session, "carriers", []);
  const carrierId = get(loginInformation, "carrier.id");
  const sessionCarrierIntegrations = carrierId
    ? get(
      sessionCarriers.find((carrierConfig: any) => carrierConfig.id === carrierId),
      "integrations",
      []
    )
    : sessionCarriers.flatMap((carrierConfig: any) =>
      get(carrierConfig, "integrations", [])
    );

  if (integration) {
    console.log("[isIntegrationV123] integration found in session carriers");
    const matchingSessionIntegration = sessionCarrierIntegrations.find(
      (integrationConfig: any) => integrationConfig.integration === integration
    );
    console.log("[isIntegrationV123] matchingSessionIntegration: ", matchingSessionIntegration);
    const result = get(matchingSessionIntegration, "type") === "V123";
    console.log("[isIntegrationV123] result: ", result);
    return result;
  }
  console.log("[isIntegrationV123] no integration found in session carriers");

  return false

};

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));